<!--
  Mobile Controls - Simplified Hybrid System
  
  Test Results Table:
  | #  | Scenario                           | Layout    | Left Side                    | Right Side                           |
  |----|------------------------------------|-----------|------------------------------|--------------------------------------|
  | 1  | Map Only                          | Horizontal| Map                          | -                                    |
  | 2  | Personalization Only              | Horizontal| Personalization              | -                                    |
  | 3  | Audio Only                        | Horizontal| -                            | Audio                                |
  | 4  | Description Only                  | Horizontal| Description                  | -                                    |
  | 5  | Map + Personalization             | Horizontal| Map                          | Personalization                      |
  | 6  | Map + Audio                       | Horizontal| Map                          | Audio                                |
  | 7  | Map + Description                 | Horizontal| Map                          | Description                          |
  | 8  | Personalization + Audio           | Horizontal| Personalization              | Audio                                |
  | 9  | Personalization + Description     | Horizontal| Personalization              | Description                          |
  | 10 | Description + Audio               | Horizontal| Description                  | Audio                                |
  | 11 | Map + Personalization + Audio     | Vertical  | Map (bottom-left)            | Personalization + Audio (stack)      |
  | 12 | Map + Personalization + Description| Vertical | Map (bottom-left)            | Personalization + Description (stack)|
  | 13 | Map + Description + Audio         | Vertical  | Map (bottom-left)            | Description + Audio (stack)          |
  | 14 | Personalization + Description + Audio| Vertical| -                           | Personalization + Description + Audio (stack)|
  | 15 | All 4 Components                  | Vertical  | Map (bottom-left)            | Personalization + Description + Audio (stack)|
  
  Key Rules:
  - Center zone (201px) always preserved - no components ever centered
  - Map always left in horizontal, bottom-left in vertical
  - Audio always right in horizontal, last in vertical stack
  - Vertical stack order: Personalization → Description → Audio
  - Layout decision: useVerticalLayout = componentCount > 2
-->

<script>
  import { createEventDispatcher } from 'svelte';
  import Icon from '@iconify/svelte';
  import PersonalizationButton from './PersonalizationButton.svelte';
  import MapButton from './MapButton.svelte';
  import Audio from './Audio.svelte';
  import MobileSceneDescription from './MobileSceneDescription.svelte';
  
  const dispatch = createEventDispatcher();
  
  // Props
  export let hasPersonalization = false;
  export let hasDescription = false;
  export let hasAudio = false;
  export let hasMap = false;
  export let expandedMap = false;
  export let showLocationMenu = true;
  export let tour = null;
  export let playing = true;
  export let loop = false;
  export let resumable = true;
  export let debug = false;
  export let enableSceneAudio = true;
  export let enableDescriptionAudio = true;
  export let onPlayingChange = null;
  export let theme = null;
  /** When map is expanded on mobile, call to open/toggle the Locations sheet. */
  export let onOpenLocations = () => {};
  /** When true, Locations sheet is open (for active state on Locations button). */
  export let locationsSheetOpen = false;
  
  // Mobile detection
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  // Simple component availability check
  $: hasAudioInTour = hasAudio && tour?.scenes?.some(s => s.audioId || s.descriptionAudio?.assetId);
  
  // Count total available components
  $: componentCount = [hasMap, hasPersonalization, hasAudioInTour, hasDescription].filter(Boolean).length;
  
  // Determine if we should use vertical layout (3+ components)
  $: useVerticalLayout = componentCount > 2;
  
  // Event handlers
  function handlePersonalize() {
    dispatch('personalize');
  }

  function handleDescriptionOpen(e) {
    dispatch('descriptionOpen', e.detail);
  }

  function handleMapClick() {
    dispatch('mapClick');
  }

  function handleOpenLocations() {
    onOpenLocations({ focusFilter: false });
  }

  function handleOpenSearch() {
    onOpenLocations({ focusFilter: true });
  }
</script>

{#if isMobile}
  <div class="mobile-controls-wrapper">
    <!-- Map bottom bar: Virtual Tour | Locations | Search (only when map is expanded) -->
    {#if hasMap && expandedMap}
      <nav class="mobile-map-menu-bar" aria-label="Map actions">
        <button type="button" class="mobile-map-menu-item" on:click={handleMapClick} aria-label="Switch to virtual tour">
          <Icon icon="iconoir:view-360" width="24" height="24" aria-hidden="true" />
          <span class="mobile-map-menu-label">Virtual Tour</span>
        </button>
        {#if showLocationMenu}
        <button type="button" class="mobile-map-menu-item" class:active={locationsSheetOpen} on:click={handleOpenLocations} aria-label={locationsSheetOpen ? 'Close locations' : 'Open locations'} aria-pressed={locationsSheetOpen}>
          <Icon icon="mdi:locations-outline" width="24" height="24" aria-hidden="true" />
          <span class="mobile-map-menu-label">Locations</span>
        </button>
        <button type="button" class="mobile-map-menu-item" on:click={handleOpenSearch} aria-label="Search locations">
          <Icon icon="mdi:search" width="24" height="24" aria-hidden="true" />
          <span class="mobile-map-menu-label">Search</span>
        </button>
        {/if}
      </nav>
    {/if}
    {#if useVerticalLayout}
      <!-- Map button in bottom left for vertical layout (independent positioning) -->
      {#if hasMap}
        <div class="map-bottom-left">
          <MapButton click={handleMapClick} {expandedMap} mobileOnly={true} bgColor={theme?.primaryColor} />
        </div>
      {/if}
      
      <!-- Vertical Stack Layout for other controls (3+ components) -->
      <div class="mobile-controls-vertical">
        <div class="vertical-stack">
          {#if hasPersonalization}
            <div class="control-item" class:hidden={expandedMap}>
              <PersonalizationButton on:personalize={handlePersonalize} />
            </div>
          {/if}
          
          {#if hasDescription}
            <div class="control-item" class:hidden={expandedMap}>
              <MobileSceneDescription {tour} {hasDescription} on:descriptionOpen={handleDescriptionOpen} />
            </div>
          {/if}
          
          {#if hasAudioInTour}
            <div class="control-item" class:hidden={expandedMap}>
              <Audio
                {tour}
                {playing}
                {loop}
                {resumable}
                {debug}
                {enableSceneAudio}
                {enableDescriptionAudio}
                {onPlayingChange}
                autoInit={true}
              />
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <!-- Horizontal Layout for 1-2 components -->
      <div class="mobile-controls">
        <!-- Left Controls -->
        <div class="left-controls">
          {#if hasMap}
            <div class="control-container">
              <MapButton click={handleMapClick} {expandedMap} mobileOnly={true} bgColor={theme?.primaryColor} />
            </div>
          {/if}
          
          {#if hasPersonalization && !hasMap}
            <div class="control-container">
              <PersonalizationButton on:personalize={handlePersonalize} />
            </div>
          {/if}
          
          {#if hasDescription && !hasMap && !hasPersonalization}
            <div class="control-container">
              <MobileSceneDescription {tour} {hasDescription} on:descriptionOpen={handleDescriptionOpen} />
            </div>
          {/if}
        </div>
        
        <!-- Right Controls -->
        <div class="right-controls">
          {#if hasPersonalization && hasMap}
            <div class="control-container" class:hidden={expandedMap}>
              <PersonalizationButton on:personalize={handlePersonalize} />
            </div>
          {/if}
          
          {#if hasAudioInTour}
            <div class="control-container" class:hidden={expandedMap}>
              <Audio
                {tour}
                {playing}
                {loop}
                {resumable}
                {debug}
                {enableSceneAudio}
                {enableDescriptionAudio}
                {onPlayingChange}
                autoInit={true}
              />
            </div>
          {/if}
          
          {#if hasDescription && (hasMap || hasPersonalization)}
            <div class="control-container" class:hidden={expandedMap}>
              <MobileSceneDescription {tour} {hasDescription} on:descriptionOpen={handleDescriptionOpen} />
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
{/if}

<style>
  .mobile-controls {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    z-index: 1000;
    pointer-events: none;
  }

  .left-controls, .right-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: auto;
  }

  .control-container {
    display: flex;
    align-items: center;
  }

  .control-container.hidden {
    display: none;
  }

  /* Vertical layout styles */
  .mobile-controls-vertical {
    position: fixed;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    pointer-events: none;
  }

  .vertical-stack {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0;
    pointer-events: auto;
  }

  .control-item {
    display: flex;
    align-items: center;
  }

  .control-item.hidden {
    display: none;
  }

  .map-bottom-left {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 10px;
    z-index: 1000;
    pointer-events: auto;
  }

  /* Targeted overrides for child components within mobile controls */
  .mobile-controls-wrapper :global(.personalization-button) {
    position: static !important;
    margin: 0 !important;
  }

  .mobile-controls-wrapper :global(.container) {
    position: static !important;
    margin: 0 !important;
  }

  .mobile-controls-wrapper :global(.map-button) {
    position: static !important;
    margin: 0 !important;
  }

  /* Map expanded: bottom bar with Locations, Virtual Tour, Search (above locations sheet 1101) */
  .mobile-map-menu-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-around;
    align-items: center;
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    background: #000;
    color: #fff;
    z-index: 1102;
    pointer-events: auto;
  }

  .mobile-map-menu-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
    padding: 4px 12px;
    border-radius: 8px;
    opacity: 0.9;
    transition: opacity 0.2s ease, background-color 0.2s ease;
  }

  .mobile-map-menu-item:hover {
    opacity: 1;
  }

  .mobile-map-menu-item.active {
    opacity: 1;
    background: #333;
    color: #fff;
  }

  .mobile-map-menu-label {
    font-size: 11px;
    font-family: var(--font-body), sans-serif;
  }
</style>
